
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArticleAuthor } from "@/types/article-types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ImageUploader from "../articles/ImageUploader";
import { Loader2 } from "lucide-react";

interface AuthorFormDialogProps {
  open: boolean;
  onClose: () => void;
  author: ArticleAuthor | null;
  onSubmit: (data: Partial<ArticleAuthor>) => Promise<ArticleAuthor | null> | Promise<boolean>;
  uploadMedia: (file: File, path: string) => Promise<string | null>;
}

const AuthorFormDialog = ({
  open,
  onClose,
  author,
  onSubmit,
  uploadMedia
}: AuthorFormDialogProps) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [bio, setBio] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (author) {
      setFirstName(author.first_name || "");
      setLastName(author.last_name || "");
      setBio(author.bio || "");
      setProfileImageUrl(author.profile_image_url);
    } else {
      setFirstName("");
      setLastName("");
      setBio("");
      setProfileImageUrl(undefined);
    }
    setError(null);
  }, [author, open]);

  const handleProfileImageUpload = async (file: File) => {
    try {
      const path = "authors/profiles";
      const url = await uploadMedia(file, path);
      if (url) {
        setProfileImageUrl(url);
      }
    } catch (err) {
      console.error("Error uploading profile image:", err);
      setError("Failed to upload profile image. Please try again.");
    }
  };

  const clearProfileImage = () => {
    setProfileImageUrl(undefined);
  };

  const handleSubmit = async () => {
    if (!firstName.trim()) {
      setError("First name is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      const authorData: Partial<ArticleAuthor> = {
        first_name: firstName,
        last_name: lastName,
        bio: bio || undefined,
        profile_image_url: profileImageUrl
      };

      await onSubmit(authorData);
      onClose();
    } catch (err: any) {
      console.error("Error submitting author data:", err);
      setError(err.message || "Failed to save author data. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{author ? "Edit Author" : "Create New Author"}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 my-4">
          <div className="space-y-2">
            <Label htmlFor="profileImage">Profile Image</Label>
            <div className="flex items-center space-x-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={profileImageUrl || ''} />
                <AvatarFallback>
                  {firstName?.[0]}{lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              <ImageUploader
                currentImageUrl={profileImageUrl}
                onUpload={handleProfileImageUpload}
                onClear={clearProfileImage}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="firstName" className="required">First Name</Label>
            <Input 
              id="firstName" 
              value={firstName} 
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter first name"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input 
              id="lastName" 
              value={lastName} 
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter last name"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea 
              id="bio" 
              value={bio} 
              onChange={(e) => setBio(e.target.value)}
              placeholder="A brief biography of the author"
              rows={4}
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm mt-2">{error}</div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              author ? "Update" : "Create"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AuthorFormDialog;
